import { useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import CrumpledPaper, { OUTLINE_SHEET, OUTLINE_BUCKLE, OUTLINE_WAD, OUTLINE_BALL } from '../common/CrumpledPaper';

// Rant Room: write it out, crumple it, toss it.
//
// ⚠️ THE TEXT NEVER LEAVES THE DEVICE. It lives in this component's state and
// nowhere else — no Supabase, no postDraft, no localStorage/sessionStorage, no
// analytics. Members will vent here about their workplaces and the people they
// support, so storing nothing means nothing to leak, subpoena or moderate.
// That is also why this is a plain <textarea> and NOT PostEditor: PostEditor's
// @mention suggestions send typed fragments to the server.
//
// Animation numbers were signed off on the prototype
// (https://claude.ai/artifact/76YnGvLnZe2E3ZvHyTJEVU) and carried over unchanged.

const headerStyle = {
  backgroundColor: 'var(--header-bg)',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
  paddingTop: 'var(--sat)',
};

// Lined paper. 26px rules under a 26px line-height, offset by the top padding.
const paperText = {
  fontSize: 15,
  lineHeight: '26px',
  color: 'var(--text-primary)',
  backgroundImage: 'repeating-linear-gradient(transparent 0 25px, rgba(77,119,94,0.12) 25px 26px)',
  backgroundPosition: '0 12px',
};
const PAPER_CLASS = 'w-full h-full px-3.5 py-3 bg-transparent border-0 resize-none outline-none';

const BALL = 64;        // px, finished ball
const BIN_W = 74;
const BIN_H = 84;

function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

const RantRoomScreen = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  // writing → tossing (snapshot animates) → done
  const [phase, setPhase] = useState('writing');
  // The text being animated away. Held only for the ~1.5s of the toss, then
  // dropped; `text` itself is cleared the moment the button is pressed.
  const [toss, setToss] = useState(null);

  const rootRef = useRef(null);
  const paperRef = useRef(null);
  const mainRef = useRef(null);
  const textareaRef = useRef(null);
  const tossRefs = { x: useRef(null), y: useRef(null), spin: useRef(null), ball: useRef(null), words: useRef(null), folds: useRef(null), bin: useRef(null) };

  const crumple = () => {
    if (phase !== 'writing' || !text.trim()) return;
    textareaRef.current?.blur();      // drop the keyboard before anything moves
    const root = rootRef.current.getBoundingClientRect();
    const p = paperRef.current.getBoundingClientRect();
    const rect = { left: p.left - root.left, top: p.top - root.top, width: p.width, height: p.height };
    setToss({ text, rect });
    setText('');
    setPhase('tossing');
  };

  // Runs the signed-off sequence once the snapshot is in the DOM.
  useLayoutEffect(() => {
    if (phase !== 'tossing' || !toss) return undefined;
    let cancelled = false;
    const { x, y, spin, ball, words, folds, bin } = tossRefs;
    const canAnimate = typeof ball.current?.animate === 'function';
    const run = (el, frames, opts) => el.animate(frames, { fill: 'forwards', ...opts });

    const finish = () => {
      if (cancelled) return;
      setToss(null);                  // the snapshot — the last copy of the text — is gone
      setPhase('done');
    };

    (async () => {
      if (!canAnimate) { finish(); return; }
      if (prefersReducedMotion()) {
        await run(x.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 350, easing: 'ease-out' }).finished;
        await run(mainRef.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 150 }).finished;
        finish();
        return;
      }
      const { width, height } = toss.rect;
      // 1. Crumple into a ball (650ms); the bin rises in as it does.
      run(bin.current, [{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay: 250, easing: 'ease-out' });
      run(words.current, [{ opacity: 1, filter: 'blur(0)' }, { opacity: 0, filter: 'blur(2px)' }], { duration: 300, easing: 'ease-in' });
      run(folds.current, [{ opacity: 0 }, { opacity: 0.6, offset: 0.4 }, { opacity: 1 }], { duration: 650 });
      await run(ball.current, [
        { transform: 'none', clipPath: OUTLINE_SHEET },
        { transform: 'scale(0.86, 0.72) rotate(-3deg) skewX(5deg)', clipPath: OUTLINE_BUCKLE, offset: 0.3 },
        { transform: 'scale(0.42, 0.38) rotate(7deg) skewY(-4deg)', clipPath: OUTLINE_WAD, offset: 0.65 },
        { transform: `scale(${BALL / width}, ${BALL / height}) rotate(-12deg)`, clipPath: OUTLINE_BALL },
      ], { duration: 650, easing: 'cubic-bezier(.5,0,.6,1)' }).finished;
      if (cancelled) return;

      // 2. A beat, then an arc into the bin while spinning (80ms + 650ms).
      const b = binPos(toss.rect);
      const dx = (b.left + BIN_W / 2) - (toss.rect.left + width / 2);
      const dy = (b.top + 30) - (toss.rect.top + height / 2);
      const T = 650, D = 80;
      run(x.current, [{ transform: 'translateX(0)' }, { transform: `translateX(${dx}px)` }], { duration: T, delay: D, easing: 'linear' });
      run(y.current, [
        { transform: 'translateY(0)', easing: 'cubic-bezier(.25,.6,.45,1)' },
        { transform: 'translateY(-150px)', offset: 0.42, easing: 'cubic-bezier(.55,0,.85,.45)' },
        { transform: `translateY(${dy}px)` },
      ], { duration: T, delay: D });
      await run(spin.current, [{ transform: 'rotate(0)' }, { transform: 'rotate(560deg)' }], { duration: T, delay: D, easing: 'ease-in' }).finished;
      if (cancelled) return;

      // 3. The bin rocks once (320ms), then the screen gives way.
      await run(bin.current, [
        { transform: 'rotate(0)' }, { transform: 'rotate(-7deg)', offset: 0.3 },
        { transform: 'rotate(4deg)', offset: 0.65 }, { transform: 'rotate(0)' },
      ], { duration: 320, easing: 'ease-out' }).finished;
      run(bin.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 250, delay: 150 });
      await run(mainRef.current, [{ opacity: 1 }, { opacity: 0 }], { duration: 200 }).finished;
      finish();
    })().catch(finish);               // an interrupted animation must still end on "done"

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, toss]);

  const writeAnother = () => {
    setPhase('writing');
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const backToRelax = () => navigate('/explore/relax');

  // Lower right of the sheet, overlapping the button row; the screen fades
  // away right after, so covering it for a moment is fine.
  function binPos(rect) {
    return { left: rect.left + rect.width - BIN_W - 10, top: rect.top + rect.height - BIN_H + 56 };
  }

  return (
    <div className="min-h-screen pb-nav">
      <div className="sticky top-0 z-20 md:hidden" style={headerStyle}>
        <div className="max-w-2xl mx-auto px-4 py-2">
          <button
            onClick={backToRelax}
            className="flex items-center gap-1 mb-3 text-sm transition-colors text-white/70 hover:text-white"
          >
            <ChevronLeft size={18} />
            Relax
          </button>
          <h1 className="text-xl font-bold text-white tracking-tight">Rant Room</h1>
        </div>
      </div>

      <div ref={rootRef} className="relative max-w-2xl mx-auto px-4 pt-4 md:pt-5">
        <div className="hidden md:block mb-3">
          <button
            onClick={backToRelax}
            className="flex items-center gap-1 mb-2 text-sm transition-opacity hover:opacity-80"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <ChevronLeft size={16} />
            Relax
          </button>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Rant Room</h2>
        </div>

        {/* ⚠️ The keys are load-bearing. The toss fades the writing view to
            opacity 0 with fill: forwards; without distinct keys React reuses
            that same <div> for the "done" view, which then inherits the
            invisible animation — a blank screen after every crumple. */}
        {phase === 'done' ? (
          <div key="done" className="animate-screenFade flex flex-col items-center text-center px-6 py-16 gap-2.5">
            <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true">
              <circle cx="28" cy="28" r="27" fill="rgba(77,119,94,0.12)" />
              <path d="M18 29 Q24 24 28 29 T38 29" fill="none" stroke="var(--brand-primary)" strokeWidth="2.6" strokeLinecap="round" />
            </svg>
            <h3 className="mt-1.5 text-[22px] font-extrabold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Gone. Take a breath.
            </h3>
            <p className="text-sm leading-relaxed max-w-[250px]" style={{ color: 'var(--text-tertiary)' }}>
              Nothing was saved. It only ever existed on this screen.
            </p>
            <div className="flex flex-col gap-2 w-full max-w-[260px] mt-2.5">
              <button
                onClick={writeAnother}
                className="w-full rounded-2xl py-3 px-4 font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
                style={{ backgroundColor: 'var(--brand-primary)' }}
              >
                Write another
              </button>
              <button
                onClick={backToRelax}
                className="w-full rounded-2xl py-3 px-4 font-bold transition-all hover:brightness-95 active:scale-[0.98]"
                style={{ color: 'var(--brand-primary)', backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
              >
                Back to Relax
              </button>
            </div>
          </div>
        ) : (
          <div key="writing" ref={mainRef} className="flex flex-col gap-3">
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Write it out. Nothing you write here is saved or sent anywhere. When you're done, crumple it.
            </p>
            <div
              ref={paperRef}
              className="relative h-[48vh] min-h-[260px] max-h-[520px] rounded-[14px] overflow-hidden"
              style={{
                backgroundColor: '#FFFEFB',
                border: '1px solid var(--card-border)',
                boxShadow: '0 2px 10px rgba(26,46,34,0.06)',
                visibility: toss ? 'hidden' : 'visible',
              }}
            >
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                aria-label="What's on your mind"
                placeholder="Go ahead. Nobody will read this."
                autoComplete="off"
                className={`${PAPER_CLASS} placeholder:text-[color:var(--text-tertiary)]`}
                style={paperText}
                disabled={phase !== 'writing'}
              />
            </div>
            <button
              onClick={crumple}
              disabled={phase !== 'writing' || !text.trim()}
              className="w-full rounded-2xl py-3 px-4 text-[15px] font-bold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-45 disabled:active:scale-100 disabled:hover:brightness-100"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Crumple it
            </button>
            <p className="text-xs text-center leading-relaxed pb-2" style={{ color: 'var(--text-tertiary)' }}>
              If you're in crisis, call or text{' '}
              <a href="tel:988" className="font-bold underline-offset-2 hover:underline" style={{ color: 'var(--text-secondary)' }}>988</a>
              {' '}any time.
            </p>
          </div>
        )}

        {toss && (
          <>
            {/* Bin: the back rim sits behind the ball, the body in front of it,
                so the ball drops in rather than passing over it. */}
            <div
              ref={tossRefs.bin}
              aria-hidden="true"
              className="absolute pointer-events-none"
              style={{ ...binPos(toss.rect), width: BIN_W, height: BIN_H, opacity: 0, transformOrigin: '50% 100%' }}
            >
              <svg className="absolute inset-0" style={{ zIndex: 3 }} viewBox="0 0 74 84">
                <ellipse cx="37" cy="14" rx="31" ry="8" fill="#2F5040" />
              </svg>
              <svg className="absolute inset-0" style={{ zIndex: 5 }} viewBox="0 0 74 84">
                <path d="M6 14 L13 78 Q14 82 18 82 L56 82 Q60 82 61 78 L68 14 Q37 26 6 14 Z" fill="#4D775E" />
                <path d="M6 14 Q37 26 68 14" fill="none" stroke="#7BAF8E" strokeWidth="3" />
                <path d="M24 30 L26 72 M37 32 L37 74 M50 30 L48 72" stroke="#3C6049" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </div>

            {/* The snapshot: x (sideways) > y (arc) > spin > ball (crumple). */}
            <div
              ref={tossRefs.x}
              aria-hidden="true"
              className="absolute pointer-events-none"
              style={{ ...toss.rect, zIndex: 4 }}
            >
              <div ref={tossRefs.y} className="absolute inset-0">
                <div ref={tossRefs.spin} className="absolute inset-0" style={{ filter: 'drop-shadow(0 3px 4px rgba(26,46,34,0.22))' }}>
                  <div ref={tossRefs.ball} className="absolute inset-0 overflow-hidden" style={{ backgroundColor: '#FFFEFB' }}>
                    <div ref={tossRefs.words} className={`${PAPER_CLASS} whitespace-pre-wrap overflow-hidden`} style={paperText}>
                      {toss.text}
                    </div>
                    <div ref={tossRefs.folds} className="absolute inset-0" style={{ opacity: 0 }}>
                      <CrumpledPaper className="block w-full h-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RantRoomScreen;
